import { useMemo, useState } from "react";
import "../styles/ProjectStackEditor.scss";

export const PROJECT_STACK_CONTOURS = [
  { key: "frontend", label: "Frontend", options: ["React", "Next.js", "Vue.js", "Nuxt", "Angular", "Svelte", "TypeScript", "HTML/CSS/JS"] },
  { key: "backend", label: "Backend", options: ["Go", "Node.js", "Python", "Java", "Kotlin", "C#/.NET", "PHP", "Ruby", "Rust"] },
  { key: "mobile", label: "Mobile", options: ["Flutter", "React Native", "Swift/iOS", "Kotlin/Android", "PWA"] },
  { key: "data_analytics", label: "Данные и аналитика", options: ["SQL", "PostgreSQL", "ClickHouse", "Python/Pandas", "Jupyter", "Power BI", "Tableau", "ML/AI"] },
  { key: "devops", label: "DevOps и инфраструктура", options: ["Docker", "Kubernetes", "Nginx", "Linux", "GitHub Actions", "GitLab CI", "Terraform", "Prometheus/Grafana"] },
  { key: "qa", label: "QA и тестирование", options: ["Playwright", "Cypress", "Jest", "Pytest", "Postman", "Selenium", "Allure"] },
  { key: "design", label: "Дизайн", options: ["Figma", "UI/UX", "Design System", "Adobe Illustrator", "Blender"] },
  { key: "other", label: "Другое", options: ["Telegram Bot", "REST API", "GraphQL", "WebSocket", "Blockchain", "IoT"] },
];

export function emptyProjectStack() {
  return {};
}

export function normalizeProjectStack(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  return Object.fromEntries(
    PROJECT_STACK_CONTOURS.map(({ key }) => [
      key,
      Array.isArray(value[key])
        ? [...new Set(value[key].map((item) => String(item).trim()).filter(Boolean))]
        : [],
    ]).filter(([, items]) => items.length > 0)
  );
}

export default function ProjectStackEditor({
  value,
  onChange,
  editable = true,
  compact = false,
}) {
  const stack = useMemo(() => normalizeProjectStack(value), [value]);
  const [customValues, setCustomValues] = useState({});

  const updateContour = (contour, items) => {
    const next = { ...stack };
    if (items.length > 0) {
      next[contour] = items;
    } else {
      delete next[contour];
    }
    onChange?.(next);
  };

  const toggleTechnology = (contour, technology) => {
    const current = stack[contour] || [];
    updateContour(
      contour,
      current.includes(technology)
        ? current.filter((item) => item !== technology)
        : [...current, technology]
    );
  };

  const addCustom = (contour) => {
    const technology = (customValues[contour] || "").trim();
    if (!technology) return;
    const current = stack[contour] || [];
    if (!current.some((item) => item.toLowerCase() === technology.toLowerCase())) {
      updateContour(contour, [...current, technology]);
    }
    setCustomValues((prev) => ({ ...prev, [contour]: "" }));
  };

  const hasAny = PROJECT_STACK_CONTOURS.some(({ key }) => stack[key]?.length);

  if (!editable && !hasAny) {
    return <p className="project-stack__empty">Стек пока не указан.</p>;
  }

  return (
    <div
      className={`project-stack${editable ? " is-editable" : ""}${compact ? " is-compact" : ""}`}
    >
      {PROJECT_STACK_CONTOURS.map(({ key, label, options }) => {
        const selected = stack[key] || [];
        if (!editable && selected.length === 0) return null;
        const visibleOptions = editable
          ? [...new Set([...options, ...selected])]
          : selected;
        return (
          <section className="project-stack__contour" key={key}>
            <h4>{label}</h4>
            <div className="project-stack__options">
              {visibleOptions.map((technology) => {
                const checked = selected.includes(technology);
                return editable ? (
                  <label className={`project-stack__option${checked ? " is-selected" : ""}`} key={technology}>
                    <input type="checkbox" checked={checked} onChange={() => toggleTechnology(key, technology)} />
                    <span>{technology}</span>
                  </label>
                ) : (
                  <span className="project-stack__option is-selected" key={technology}>{technology}</span>
                );
              })}
            </div>
            {editable && (
              <div className="project-stack__custom">
                <input
                  value={customValues[key] || ""}
                  onChange={(event) => setCustomValues((prev) => ({ ...prev, [key]: event.target.value }))}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addCustom(key);
                    }
                  }}
                  placeholder="Своя технология"
                  maxLength={80}
                />
                <button type="button" onClick={() => addCustom(key)}>Добавить</button>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
